import {
  faArrowDownToSquare,
  faArrowUpFromSquare,
  faFileCircleMinus,
  faFileCirclePlus,
  faFilePen,
  faFiles,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ButtonGroup, Toolbar } from "@progress/kendo-react-buttons";
import { getter } from "@progress/kendo-react-common";
import { Window } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn, getSelectedState } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useCallback, useEffect, useState } from "react";
import { isStageEditor } from "../../Template/NewTemplateSignals";
import "../ConditionBuilder/ConditionRuleBuilder.css";
import { visibilityBuilderVisibility } from "../Store/CommonSignals";
import VisibilityRuleEditor from "./VisibilityRuleEditor";

const VisibilityRuleBuilder = ({ handlePropertyChange, currentPropertyValues }) => {
  const [select] = useState({});
  const internalRules = currentPropertyValues?.VisibilityRules || [];
  const [maxId, setMaxId] = useState(0);
  const SELECTED_FIELD = "selected";
  const idGetter = getter("id");
  const [selectedState, setSelectedState] = useState({});
  const [toggleEditRule, setToggleEditRule] = useState(false);

  const [dataState, setDataState] = useState(
    internalRules?.map((dataItem) => ({
      selected: false,
      ...dataItem,
    })) || []
  );

  const cellWithEnabledCheckbox = (props) => {
    return (
      <td>
        <Checkbox
          value={props.dataItem[props.field]}
          onChange={(e) => {
            //uncheck all other checkboxes
            const newSelectedState = {};
            dataState.forEach((item) => {
              newSelectedState[item.id] = false;
            });
            newSelectedState[props.dataItem.id] = e.value;
            setSelectedState(newSelectedState);
          }}
        />
      </td>
    );
  };

  useEffect(() => {
    const newMaxId = Math.max(...(dataState.length > 0 ? dataState.map((item) => item.id) : [0]));
    setMaxId(newMaxId);
  }, [dataState]);

  const cellWithCheckbox = (props) => {
    return (
      <td readOnly={true}>
        <Checkbox
          value={props.dataItem[props.field]}
          onChange={(e) => {
            //find the id of the matching rule and update the correct value
            const newRules = [...dataState];
            let selectedRule = dataState.find((item) => item.id === props.dataItem.id);
            selectedRule = { ...selectedRule, [props.field]: e.value };
            newRules[newRules.findIndex((item) => item.id === selectedRule.id)] = selectedRule;
            setDataState([...newRules]);
          }}
        />
      </td>
    );
  };

  //#region UI Interactions
  const handleSubmit = () => {
    const cleanedDataState = dataState.map(({ selected, ...item }) => item);
    handlePropertyChange({ propName: "VisibilityRules", propValue: cleanedDataState });
    visibilityBuilderVisibility.value = false;
  };

  const onSelectionChange = useCallback(
    (event) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: "id",
      });
      setSelectedState(newSelectedState);
    },
    [selectedState]
  );

  const handleNewRule = () => {
    const newRules = [...dataState];
    newRules.push({
      id: maxId + 1,
      stage: "",
      expression: "",
      stopProcessing: false,
      disableIfTrue: false,
      selected: false,
    });
    setMaxId(maxId + 1);
    setDataState([...newRules]);
  };

  const handleEditRule = () => {
    if (
      dataState
        .map((item) => ({
          ...item,
          [SELECTED_FIELD]: selectedState[idGetter(item)],
        }))
        .filter((item) => item[SELECTED_FIELD])[0]
    ) {
      setToggleEditRule(!toggleEditRule);
    }
  };

  const handleDuplicateRule = () => {
    const newRules = [...dataState];

    const selectedRule = dataState
      .map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
      }))
      .filter((item) => item[SELECTED_FIELD])[0];
    selectedRule.id = maxId + 1;
    selectedRule.selected = false;
    setDataState([...newRules, selectedRule]);
  };

  const handleDeleteRule = () => {
    const newRules = [...dataState];

    const selectedRule = dataState
      .map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
      }))
      .filter((item) => item[SELECTED_FIELD])[0];
    const index = newRules.findIndex((item) => item.id === selectedRule.id);
    newRules.splice(index, 1);
    setDataState([...newRules]);
  };

  const handleMoveUp = () => {
    const newRules = [...dataState];

    const selectedRule = dataState
      .map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
      }))
      .filter((item) => item[SELECTED_FIELD])[0];

    const index = newRules.findIndex((item) => item.id === selectedRule.id);

    if (index > 0) {
      // Swap positions
      const tempRule = newRules[index - 1];
      newRules[index - 1] = newRules[index];
      newRules[index] = tempRule;

      // Swap id values
      const tempId = newRules[index - 1].id;
      newRules[index - 1].id = newRules[index].id;
      newRules[index].id = tempId;

      // Swap selected values
      newRules[index - 1][SELECTED_FIELD] = true;
      newRules[index][SELECTED_FIELD] = false;

      // Update the selectedState
      const newSelectedState = { ...selectedState };
      newSelectedState[tempId] = false; // The item that's now below
      newSelectedState[newRules[index - 1].id] = true; // The item that's now on the position of the selected rule
      setSelectedState(newSelectedState);
    }

    setDataState([...newRules]);
  };

  const handleMoveDown = () => {
    const newRules = [...dataState];

    const selectedRule = dataState
      .map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
      }))
      .filter((item) => item[SELECTED_FIELD])[0];

    const index = newRules.findIndex((item) => item.id === selectedRule.id);

    // Check if the item is not the last one
    if (index < newRules.length - 1) {
      // Swap positions
      const tempRule = newRules[index + 1];
      newRules[index + 1] = newRules[index];
      newRules[index] = tempRule;

      // Swap id values
      const tempId = newRules[index + 1].id;
      newRules[index + 1].id = newRules[index].id;
      newRules[index].id = tempId;

      // Swap selected values
      newRules[index + 1][SELECTED_FIELD] = true;
      newRules[index][SELECTED_FIELD] = false;

      // Update the selectedState
      const newSelectedState = { ...selectedState };
      newSelectedState[tempId] = false; // The item that's now above
      newSelectedState[newRules[index + 1].id] = true; // The item that's now on the position of the selected rule
      setSelectedState(newSelectedState);
    }

    setDataState([...newRules]);
  };
  //#endregion UI Interactions

  return (
    <div className="condition-rule-page-content">
      <div className="condition-rule-container">
        <Toolbar>
          <ButtonGroup>
            <Button onClick={handleNewRule}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faFileCirclePlus}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                New Rule
              </div>
            </Button>
            <Button onClick={handleEditRule}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faFilePen}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                Edit Rule
              </div>
            </Button>
            <Button onClick={handleDeleteRule}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  className="toolbar-button-icon"
                  icon={faFileCircleMinus}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                Delete Rule
              </div>
            </Button>
            <Button onClick={handleDuplicateRule}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faFiles}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                Duplicate Rule
              </div>
            </Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button onClick={handleMoveUp}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faArrowUpFromSquare}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
              </div>
            </Button>
            <Button onClick={handleMoveDown}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faArrowDownToSquare}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
              </div>
            </Button>
          </ButtonGroup>
        </Toolbar>
        <Grid
          className="rules-grid k-w-full"
          data={dataState.map((item) => ({
            ...item,
            [SELECTED_FIELD]: selectedState[idGetter(item)],
          }))}
          key="selected"
          dataItemKey="id"
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: "single",
          }}
          onSelectionChange={onSelectionChange}
          select={select}
        >
          <GridColumn
            field={SELECTED_FIELD}
            width="50px"
            title=" "
            cells={{ data: cellWithEnabledCheckbox }}
          />
          {isStageEditor.value && (
            <GridColumn
              width="150px"
              field="stage"
              title="Stage"
            />
          )}
          <GridColumn
            width="250px"
            field="expression"
            title="Rule (applied in order shown)"
          />
          <GridColumn
            field="stopProcessing"
            title="Stop if True"
            cells={{
              data: cellWithCheckbox,
            }}
          />
          <GridColumn
            field="disableIfTrue"
            title="Disable if True"
            cells={{
              data: cellWithCheckbox,
            }}
          />
        </Grid>
      </div>
      <div className="expression-builder-buttons">
        <Button
          themeColor={"success"}
          onClick={() => handleSubmit()}
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
        >
          Save
        </Button>
        <Button
          themeColor={"info"}
          onClick={() => (visibilityBuilderVisibility.value = false)}
          className="k-ml-3 k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
        >
          Cancel
        </Button>
      </div>
      {toggleEditRule && (
        <Window
          onClose={handleEditRule}
          className="condition-builder-window"
          initialHeight={900}
          initialWidth={1600}
          title={"Edit Visibility Rule"}
        >
          <VisibilityRuleEditor
            rule={
              dataState
                ?.map((item) => ({
                  ...item,
                  [SELECTED_FIELD]: selectedState[idGetter(item)],
                  text: item.stage,
                }))
                ?.filter((item) => item[SELECTED_FIELD])[0]
            }
            onClose={() => {
              setToggleEditRule(!toggleEditRule);
            }}
            dataState={dataState}
            setDataState={setDataState}
          />
        </Window>
      )}
    </div>
  );
};

export default VisibilityRuleBuilder;
